import { useState, useEffect, useCallback } from 'react';
import { X, Loader2, Printer, FileDown, RefreshCw } from 'lucide-react';
import { serviceCallsAPI } from '@shared/apiClient';
import { getStatusText } from '@shared/utils';
import toast from 'react-hot-toast';
import {
  SERVICE_CALL_LIST_COLUMNS,
  DEFAULT_EXPORT_COLUMN_IDS,
  buildListTableRows,
  downloadServiceCallListPdf,
  printServiceCallListPdf
} from '../utils/serviceCallListPdf';

const SORT_OPTIONS = [
  { value: 'clientName', label: 'Client name' },
  { value: 'scheduledDate', label: 'Scheduled date' },
  { value: 'completedDate', label: 'Completed date' },
  { value: 'createdAt', label: 'Created date' },
  { value: 'updatedAt', label: 'Updated date' },
  { value: 'technicianName', label: 'Technician' }
];

const EXPORT_TIMEOUT_MS = 120000;

const SEGMENT_LABELS = {
  all: 'All',
  cspire: 'C Spire only',
  franklin: 'Franklin only',
  commercial: 'Commercial only',
  residential: 'Residential only'
};

/**
 * @param {object} filters
 * @param {number} page
 */
function buildServiceCallsQuery(filters, page) {
  const {
    searchTerm,
    clientFilter,
    technicianIds,
    statusFilter,
    priorityFilter,
    typeFilter,
    warrantyFilter,
    finalizedFilter,
    dateFrom,
    dateTo,
    dateRangeField,
    segmentFilter,
    sortBy,
    sortOrder
  } = filters;

  const params = {
    page,
    limit: 10000,
    sortBy,
    sortOrder,
    ...(searchTerm && { search: searchTerm }),
    ...(clientFilter && { clientId: clientFilter }),
    ...(statusFilter && { status: statusFilter }),
    ...(priorityFilter && { priority: priorityFilter }),
    ...(typeFilter && { typeId: typeFilter }),
    ...(warrantyFilter !== '' && { isWarranty: warrantyFilter === 'true' }),
    ...(finalizedFilter !== '' && { isFinalized: finalizedFilter }),
    ...(dateFrom && { dateFrom }),
    ...(dateTo && { dateTo }),
    ...(dateRangeField && dateRangeField !== 'scheduled' && { dateRangeField }),
    ...(segmentFilter && segmentFilter !== 'all' && { segment: segmentFilter }),
    ...(technicianIds?.length > 0 && { technicianIds: technicianIds.join(',') })
  };
  return params;
}

/**
 * @param {object} filters
 * @param {{ id: string, companyName?: string }[]} clients
 * @param {{ id: string, firstName?: string, lastName?: string }[]} technicians
 * @param {{ id: string, name?: string }[]} serviceCallTypes
 */
function buildFilterSummaryLines(filters, clients, technicians, serviceCallTypes) {
  const lines = [];
  const {
    searchTerm,
    clientFilter,
    technicianIds,
    statusFilter,
    priorityFilter,
    typeFilter,
    warrantyFilter,
    finalizedFilter,
    dateFrom,
    dateTo,
    dateRangeField,
    segmentFilter,
    sortBy,
    sortOrder
  } = filters;

  if (searchTerm) lines.push(`Search: ${searchTerm}`);
  if (clientFilter) {
    const c = clients.find((cl) => cl.id === clientFilter);
    lines.push(`Customer: ${c?.companyName || clientFilter}`);
  }
  if (technicianIds?.length > 0) {
    const names = technicianIds
      .map((id) => {
        const t = technicians.find((u) => u.id === id);
        return t ? `${t.firstName || ''} ${t.lastName || ''}`.trim() : id;
      })
      .join('; ');
    lines.push(`Technicians: ${names}`);
  } else {
    lines.push('Technicians: All');
  }
  if (statusFilter) lines.push(`Status: ${getStatusText(statusFilter)}`);
  if (priorityFilter) lines.push(`Priority: ${getStatusText(priorityFilter)}`);
  if (typeFilter) {
    const ty = serviceCallTypes.find((t) => t.id === typeFilter);
    lines.push(`Type: ${ty?.name || typeFilter}`);
  }
  if (warrantyFilter === 'true') lines.push('Warranty: Yes');
  if (warrantyFilter === 'false') lines.push('Warranty: No');
  if (finalizedFilter === 'true') lines.push('Finalized: Yes');
  if (finalizedFilter === 'false') lines.push('Finalized: No');
  const dr = dateRangeField === 'completed' ? 'Completed date' : 'Scheduled date';
  if (dateFrom || dateTo) {
    lines.push(`Date range (${dr}): ${dateFrom || '…'} – ${dateTo || '…'}`);
  }
  if (segmentFilter && segmentFilter !== 'all') {
    lines.push(`Account / site: ${SEGMENT_LABELS[segmentFilter] || segmentFilter}`);
  }
  const sortLabel = SORT_OPTIONS.find((o) => o.value === sortBy)?.label || sortBy;
  lines.push(`Sort: ${sortLabel} (${sortOrder})`);
  return lines;
}

export default function ServiceCallListExportModal({
  isOpen,
  onClose,
  /** Snapshot of list filters + sort from Service Calls page */
  initialFilters,
  technicians = [],
  clients = [],
  serviceCallTypes = []
}) {
  const [filters, setFilters] = useState(() => ({ ...initialFilters }));
  const [selectedColumnIds, setSelectedColumnIds] = useState(() => [...DEFAULT_EXPORT_COLUMN_IDS]);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [fetchError, setFetchError] = useState(null);

  useEffect(() => {
    if (isOpen && initialFilters) {
      setFilters({
        ...initialFilters,
        segmentFilter: initialFilters.segmentFilter || 'all'
      });
      setSelectedColumnIds([...DEFAULT_EXPORT_COLUMN_IDS]);
      setRows([]);
      setFetchError(null);
    }
  }, [isOpen, initialFilters]);

  const toggleColumn = (id) => {
    setSelectedColumnIds((prev) => {
      if (prev.includes(id)) {
        if (prev.length <= 1) return prev;
        return prev.filter((x) => x !== id);
      }
      return [...prev, id];
    });
  };

  const fetchAllServiceCalls = useCallback(async () => {
    setLoading(true);
    setFetchError(null);
    const merged = [];
    try {
      let page = 1;
      while (true) {
        const params = buildServiceCallsQuery(filters, page);
        const { data } = await serviceCallsAPI.getServiceCalls(params, {
          timeout: EXPORT_TIMEOUT_MS
        });
        const batch = data?.serviceCalls || [];
        merged.push(...batch);
        const pag = data?.pagination;
        if (!pag?.hasNext || batch.length === 0) break;
        page += 1;
      }
      setRows(merged);
      if (merged.length === 0) {
        toast.error('No service calls match these filters.');
      } else {
        toast.success(`Loaded ${merged.length} service call(s).`);
      }
    } catch (err) {
      console.error(err);
      const msg = err.response?.data?.message || err.message || 'Failed to load service calls';
      setFetchError(msg);
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  }, [filters]);

  const pdfOptions = () => ({
    columnIds: selectedColumnIds,
    title: 'Service calls',
    filterSummaryLines: buildFilterSummaryLines(filters, clients, technicians, serviceCallTypes)
  });

  const handleDownload = () => {
    if (!rows.length) {
      toast.error('Load preview data first.');
      return;
    }
    downloadServiceCallListPdf(rows, pdfOptions());
  };

  const handlePrint = () => {
    if (!rows.length) {
      toast.error('Load preview data first.');
      return;
    }
    printServiceCallListPdf(rows, pdfOptions());
  };

  const previewRows = buildListTableRows(rows, selectedColumnIds);
  const previewHeaders = selectedColumnIds.map(
    (id) => SERVICE_CALL_LIST_COLUMNS.find((c) => c.id === id)?.label || id
  );

  if (!isOpen) return null;

  const activeTechs = technicians.filter((t) => t.isActive !== false);

  return (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
      <div className="relative top-8 mx-auto p-5 border w-full max-w-6xl shadow-lg rounded-md bg-white mb-10">
        <div className="flex items-center justify-between mb-4 pb-4 border-b">
          <div>
            <h3 className="text-lg font-medium text-gray-900">Print / export service call list</h3>
            <p className="text-sm text-gray-500 mt-1">
              Adjust filters and columns, load data, then preview or download a PDF.
            </p>
          </div>
          <button type="button" onClick={onClose} className="text-gray-400 hover:text-gray-500">
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
          <div className="space-y-4">
            <h4 className="text-sm font-semibold text-gray-800">Sort & date range</h4>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Sort by</label>
                <select
                  className="input w-full"
                  value={filters.sortBy}
                  onChange={(e) => setFilters((f) => ({ ...f, sortBy: e.target.value }))}
                >
                  {SORT_OPTIONS.map((o) => (
                    <option key={o.value} value={o.value}>
                      {o.label}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Order</label>
                <select
                  className="input w-full"
                  value={filters.sortOrder}
                  onChange={(e) => setFilters((f) => ({ ...f, sortOrder: e.target.value }))}
                >
                  <option value="ASC">Ascending</option>
                  <option value="DESC">Descending</option>
                </select>
              </div>
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">
                Date range applies to
              </label>
              <select
                className="input w-full"
                value={filters.dateRangeField}
                onChange={(e) => setFilters((f) => ({ ...f, dateRangeField: e.target.value }))}
              >
                <option value="scheduled">Scheduled date</option>
                <option value="completed">Completed date</option>
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">
                Account / site type
              </label>
              <select
                className="input w-full"
                value={filters.segmentFilter || 'all'}
                onChange={(e) => setFilters((f) => ({ ...f, segmentFilter: e.target.value }))}
              >
                <option value="all">All</option>
                <option value="cspire">C Spire only</option>
                <option value="franklin">Franklin only</option>
                <option value="commercial">Commercial only</option>
                <option value="residential">Residential only</option>
              </select>
            </div>
          </div>

          <div>
            <h4 className="text-sm font-semibold text-gray-800 mb-2">Technicians</h4>
            <p className="text-xs text-gray-500 mb-2">Leave none selected for all technicians.</p>
            <div className="border rounded-md max-h-40 overflow-y-auto p-2 space-y-1 bg-gray-50">
              {activeTechs.map((tech) => (
                <label key={tech.id} className="flex items-center gap-2 text-sm cursor-pointer">
                  <input
                    type="checkbox"
                    checked={filters.technicianIds?.includes(tech.id) || false}
                    onChange={(e) => {
                      setFilters((f) => {
                        const cur = new Set(f.technicianIds || []);
                        if (e.target.checked) cur.add(tech.id);
                        else cur.delete(tech.id);
                        return { ...f, technicianIds: Array.from(cur) };
                      });
                    }}
                  />
                  <span>
                    {tech.firstName} {tech.lastName}
                  </span>
                </label>
              ))}
            </div>
          </div>
        </div>

        <div className="mb-6">
          <h4 className="text-sm font-semibold text-gray-800 mb-2">Columns in PDF</h4>
          <div className="flex flex-wrap gap-2">
            {SERVICE_CALL_LIST_COLUMNS.map((col) => (
              <label
                key={col.id}
                className="inline-flex items-center gap-1.5 text-xs bg-gray-100 px-2 py-1 rounded border border-gray-200 cursor-pointer"
              >
                <input
                  type="checkbox"
                  checked={selectedColumnIds.includes(col.id)}
                  onChange={() => toggleColumn(col.id)}
                />
                {col.label}
              </label>
            ))}
          </div>
        </div>

        <div className="flex flex-wrap items-center gap-3 mb-6">
          <button
            type="button"
            onClick={fetchAllServiceCalls}
            disabled={loading}
            className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 disabled:opacity-50"
          >
            {loading ? (
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
            ) : (
              <RefreshCw className="h-4 w-4 mr-2" />
            )}
            Load / refresh data
          </button>
          <button
            type="button"
            onClick={handleDownload}
            disabled={!rows.length || loading}
            className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
          >
            <FileDown className="h-4 w-4 mr-2" />
            Download PDF
          </button>
          <button
            type="button"
            onClick={handlePrint}
            disabled={!rows.length || loading}
            className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
          >
            <Printer className="h-4 w-4 mr-2" />
            Print
          </button>
          <span className="text-sm text-gray-600">{rows.length ? `${rows.length} row(s)` : ''}</span>
        </div>

        {fetchError && (
          <div className="mb-4 bg-red-50 border border-red-200 rounded-md p-3 text-sm text-red-800">
            {fetchError}
          </div>
        )}

        <div>
          <h4 className="text-sm font-semibold text-gray-800 mb-2">Preview</h4>
          <div className="border rounded-lg overflow-auto max-h-[50vh] shadow-inner">
            <table className="min-w-full text-xs divide-y divide-gray-200">
              <thead className="bg-gray-100 sticky top-0">
                <tr>
                  {previewHeaders.map((h) => (
                    <th
                      key={h}
                      className="px-2 py-2 text-left font-semibold text-gray-700 whitespace-nowrap"
                    >
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100 bg-white">
                {previewRows.length === 0 && !loading && (
                  <tr>
                    <td
                      colSpan={Math.max(1, previewHeaders.length)}
                      className="px-3 py-8 text-center text-gray-500"
                    >
                      Load data to see a preview.
                    </td>
                  </tr>
                )}
                {previewRows.map((cells, ri) => (
                  <tr key={ri} className={ri % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                    {cells.map((cell, ci) => (
                      <td key={ci} className="px-2 py-1.5 text-gray-800 align-top max-w-[14rem] break-words">
                        {cell}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
}
